import { Component, Input, ViewChild, Output, EventEmitter } from '@angular/core';
import { DxFormComponent } from 'devextreme-angular';
import { DxDataGridComponent, DxDataGridTypes } from 'devextreme-angular/ui/data-grid';
import { SellerSnapshotDatasourceFieldType } from '../../constants/enums';
import { SellerLayout } from '../../models/seller-layout';
import { SellerSnapshotFormFieldProperties } from '../../models/seller-snapshot-form-field-properties';
import { HelperService } from '../../services/helper.service';
import List from "devextreme/ui/list";

@Component({
    selector: 'app-seller-snapshot-grid-config-popup',
    templateUrl: './seller-snapshot-grid-config-popup.component.html',
    styleUrls: ['./seller-snapshot-grid-config-popup.component.scss']
})

export class SellerSnapshotGridConfigPopupComponent {    
    @ViewChild('gridColumnsGrid', {static: false})
    gridColumnsGrid: DxDataGridComponent;
    @ViewChild('gridConfigForm', {static: false})
    gridConfigForm: DxFormComponent;

    @Input() visible: boolean;
    @Input() sellerLayout: SellerLayout;
    @Input() masterMapping: any;
    @Input() columnMappings: any;

    @Output() onSave = new EventEmitter();
    @Output() onCancel = new EventEmitter();

    fieldProps: SellerSnapshotFormFieldProperties;
    gridColumns: { column: string, columnName: string }[];
    gridColumnsHideUnselected: boolean = false;
    dateFieldsMapping: any = {};
    editGridConfigPopupSaveButtonOptions: any;
    editGridConfigPopupCancelButtonOptions: any;    
    filterBuilderFields: any[] = [];
    filterBuilderValue: any[] = [];
    filterBuilderCustomOperations: any[] = [];
    filterBuilderPlaceholderFieldCaption: string = 'Select a field...';    
    filterBuilderGroupMapping: any = {};
    gridHeightOptions: { name: string, value: number }[] = [{ name: '25%', value: 25 }, { name: '50%', value: 50 }, { name: '75%', value: 75 }, { name: '100%', value: 100 }];
    gridConfigPopupHeight: number = 600;
    gridConfigMinPopupHeight: number = 500;
    gridConfigPopupGridHeight: number;
    gridConfigPopupGridHeightDiff: number = 298;
    gridConfigPopupFilterHeight: number;
    gridConfigPopupFilterHeightDiff: number = 151;
    snapshotDateFieldName: string = 'date_SNAPSHOTDATE';
    periodBeginDateFieldName: string = 'date_PERIODBEGINDATE';
    periodEndDateFieldName: string = 'date_PERIODENDDATE';

    customFilterBuilderOperations: any[] = [
        { name: '= ', caption: 'Equals', icon: 'equal', editorTemplate: 'comboSelectBoxTemplate', customizeText: (fieldInfo) => this.customizeComboBoxText(fieldInfo) },
        { name: '<> ', caption: 'Does not equal', icon: 'notequal', editorTemplate: 'comboSelectBoxTemplate', customizeText: (fieldInfo) => this.customizeComboBoxText(fieldInfo) },
        { name: '< ', caption: 'Is less than', icon: 'less', editorTemplate: 'comboSelectBoxTemplate', customizeText: (fieldInfo) => this.customizeComboBoxText(fieldInfo) },
        { name: '> ', caption: 'Is greater than', icon: 'greater', editorTemplate: 'comboSelectBoxTemplate', customizeText: (fieldInfo) => this.customizeComboBoxText(fieldInfo) },
        { name: '<= ', caption: 'Is less than or equal to', icon: 'lessorequal', editorTemplate: 'comboSelectBoxTemplate', customizeText: (fieldInfo) => this.customizeComboBoxText(fieldInfo) },
        { name: '>= ', caption: 'Is greater than or equal to', icon: 'greaterorequal', editorTemplate: 'comboSelectBoxTemplate', customizeText: (fieldInfo) => this.customizeComboBoxText(fieldInfo) }
    ];
    
    constructor(private helperService: HelperService) {        
    }

    editGridConfig = (): void => {
        this.dateFieldsMapping = {};
        Object.keys(this.columnMappings).filter(x => x.startsWith('date_')).forEach(x => this.dateFieldsMapping[x] = this.columnMappings[x]);

        // Add dynamic date options
        this.dateFieldsMapping[this.snapshotDateFieldName] = '<Snapshot Date>';
        this.dateFieldsMapping[this.periodBeginDateFieldName] = '<Period Begin Date>';
        this.dateFieldsMapping[this.periodEndDateFieldName] = '<Period End Date>';

        this.gridConfigPopupHeight = 600;
        this.gridConfigPopupGridHeight = this.gridConfigPopupHeight - this.gridConfigPopupGridHeightDiff;
        this.gridConfigPopupFilterHeight = this.gridConfigPopupHeight - this.gridConfigPopupFilterHeightDiff;
        
        this.gridColumns = Object.keys(this.columnMappings).filter(x => this.columnMappings[x] !== '').map(x => ({ column: x, columnName: this.columnMappings[x] }));
        this.ascendSelectedGridColumns();

        this.configureFilterBuilder();    
        this.configureEditGridConfigPopupButtons();
    }

    configureEditGridConfigPopupButtons(): void {
        this.editGridConfigPopupSaveButtonOptions = {
            text: 'OK',
            disabled: (!this.sellerLayout.name || this.sellerLayout.name.length === 0),
            onClick: () => this.onSave.emit()
        };

        this.editGridConfigPopupCancelButtonOptions = {
            text: 'Cancel',
            onClick: () => this.onCancel.emit()
        };
    }    

    onResizeGridConfigPopup(e: any): void {        
        const popupHeight = Math.max(this.gridConfigMinPopupHeight, e.height);
        this.gridConfigPopupGridHeight = popupHeight - this.gridConfigPopupGridHeightDiff;
        this.gridConfigPopupFilterHeight = popupHeight - this.gridConfigPopupFilterHeightDiff;
    }

    onReorderGridFields = (e: DxDataGridTypes.RowDraggingReorderEvent) => {
        const fromIndex = this.gridColumns.findIndex(x => x.column === e.itemData.column);
        this.gridColumns.splice(fromIndex, 1);
        this.gridColumns.splice(e.toIndex, 0, e.itemData);
    }

    onColumnsGridSelectionChanged(e: any): void {
        if (this.gridColumnsHideUnselected && e.currentDeselectedRowKeys.length > 0) {
            this.hideUnselectedColumns();
        }
    }

    hideUnselectedColumns(): void {
        const filterArray = [];
        this.fieldProps.gridColumns.forEach((x, index) => {
            if (index > 0) {
                filterArray.push('or');
            }
            filterArray.push(['column', '=', x])
        });
        this.gridColumnsGrid.instance.filter(filterArray);
    }

    onColumnsGridToolbarPreparing(e: any): void {
        e.toolbarOptions.items.forEach(item => {
            if (item.name === 'searchPanel') {
                item.location = 'before';
                item.options = { width: '100%' };
            }
        });
        e.toolbarOptions.items.push({
            location: 'after',
            widget: 'dxCheckBox',
            options: {
                text: 'Hide Unselected',
                onValueChanged: (e: any) => {
                    this.gridColumnsHideUnselected = e.value;
                    this.ascendSelectedGridColumns();                    
                    this.gridColumnsGrid.instance.clearFilter();
                    if (this.gridColumnsHideUnselected) {
                        this.hideUnselectedColumns();
                    }
                }
            }
        });
    }

    ascendSelectedGridColumns(): void {
        const selectedFields = [];
        const selectedItemIndexes = [];
        for (let i = 0; i < this.gridColumns.length; i++) {
            const index = this.fieldProps.gridColumns?.indexOf(this.gridColumns[i].column);
            if (index > -1) {
                selectedItemIndexes.push(i);
                const item = this.gridColumns[i];
                selectedFields[index] = item;
            }
        }
        selectedItemIndexes.sort((a,b) => b - a).forEach(x => {
            this.gridColumns.splice(x, 1);
        });
        this.gridColumns.unshift(...selectedFields);
    }

    configureFilterBuilder(): void {
        this.filterBuilderCustomOperations = this.helperService.customFilterBuilderOperations;
        this.filterBuilderCustomOperations.push(...this.customFilterBuilderOperations);

        this.filterBuilderFields = [];
        this.gridColumns.forEach(column => {
            const props = this.getFilterBuilderFieldProperties(column.column);
            this.filterBuilderFields.push(this.helperService.createFilterBuilderField(column.columnName, props.dataField, props.dataType, props.lookup, props.editorOptions));
        });        

        // Sort the fields alphabetically
        this.filterBuilderFields = this.filterBuilderFields.sort((a, b) => a.caption.localeCompare(b.caption));

        // Add placeholder field - if we don't add placeholder, first field is selected by default
        this.filterBuilderFields.unshift({
            caption: this.filterBuilderPlaceholderFieldCaption,
            dataField: 'PLACEHOLDER'
        });

        this.filterBuilderValue = this.fieldProps.gridFilterObject;
    }

    getFilterBuilderFieldProperties(column: string): { dataField: string, dataType: string, lookup: any, editorOptions: any } {
        let fieldGroup, dataType = 'string', mapping, lookup, editorOptions = undefined, sortItemsFunction = null;
        if (column.indexOf('.')) {
            column = column.split('.')[column.split('.').length - 1];
        }
        const fieldType = this.getDatasourceFieldType(column);
        switch (fieldType) {
            case SellerSnapshotDatasourceFieldType.Seller:
                fieldGroup = 'SellerFields';
                const accountRelatedItems = [
                    { id: 'SELLERID_USER', name: '<Selected Account>' },
                    { id: 'SELLERID_USERMANAGER', name: '<Selected Account\'s Manager>' },
                    { id: 'SELLERID_USERPARENT', name: '<Selected Account\'s Parent>' }
                ];
                lookup = this.constructGroupedLookup(accountRelatedItems,
                    Object.keys(this.columnMappings).filter(x => x.indexOf('seller_id_') > -1).map(x => ({ id: x, name: this.columnMappings[x] })), this.masterMapping['seller']);
                editorOptions = this.getGroupedSelectEditorOptions();
                break;
            case SellerSnapshotDatasourceFieldType.Tag:
                fieldGroup = 'TagFields';
                mapping = this.masterMapping[column];
                break;
            case SellerSnapshotDatasourceFieldType.Qty:
                fieldGroup = 'QuantityFields';
                dataType = 'number';
                mapping = [];
                Object.keys(this.columnMappings).filter(x => x.startsWith('qty_')).forEach(x => mapping[x] = this.columnMappings[x]);
                break;
            case SellerSnapshotDatasourceFieldType.Date:
                fieldGroup = 'DateFields';
                dataType = 'date';
                mapping = this.dateFieldsMapping;
                sortItemsFunction = (items: any[]) => {
                    return items.sort((a, b) => {
                        const aId = a.id.replace('date_', '');
                        const bId = b.id.replace('date_', '');
                        if (isNaN(aId) && isNaN(bId)) {
                            return 0;
                        } else if (isNaN(aId) && !isNaN(bId)) {
                            return -1;
                        } else if (!isNaN(aId) && isNaN(bId)) {
                            return 1;
                        } else {
                            return a.name.localeCompare(b.name);
                        }
                    });
                };
                break;
            case SellerSnapshotDatasourceFieldType.Text:
                fieldGroup = 'TextFields';
                break;
            case SellerSnapshotDatasourceFieldType.Customer:
                fieldGroup = 'MiscellaneousFields';
                mapping = this.masterMapping['customer'];
                break;
            case SellerSnapshotDatasourceFieldType.Product:
                fieldGroup = 'MiscellaneousFields';
                mapping = this.masterMapping['product'];
                break;
        }

        if (!lookup) {
            lookup = mapping ? this.helperService.getFilterBuilderLookupObject(mapping, sortItemsFunction) : undefined;
        }
        return { dataField: `${fieldGroup}.${column}`, dataType, lookup, editorOptions };
    }

    constructGroupedLookup(accountItems: any[], fieldItems: any[], staticValuesMapping: any[]): any {
        const items = [];

        // Account-specific items
        accountItems.forEach(x => {
            items.push({
                id: x.id,
                name: x.name,
                group: 'FIELDS'
            })
        });

        // Other columns
        fieldItems.sort((a, b) => a.name.localeCompare(b.name)).forEach(x => {
            items.push({
                id: x.id,
                name: x.name,
                group: 'FIELDS'
            })
        });

        // Static values
        Object.keys(staticValuesMapping).sort((a, b) => staticValuesMapping[a].localeCompare(staticValuesMapping[b])).forEach(id => {
            items.push({
                id,
                name: staticValuesMapping[id],
                group: 'VALUES'
            });
        });

        const lookup = {
            dataSource: {
                store: items,
                group: 'group'
            },
            valueExpr: 'id',
            displayExpr: 'name'
        };

        return lookup;
    }
    
    getGroupedSelectEditorOptions(): any {
        return {
            grouped: true,
            useItemTextAsTitle: true,
            dropDownOptions: {
                onShown: (e: any) => {
                    const listElement = e.component
                        .content()
                        .querySelector(".dx-list");
                    const list = List.getInstance(listElement);
                    list.option("collapsibleGroups", true);
                }
            }
        }
    }

    getDatasourceFieldType(column: string): SellerSnapshotDatasourceFieldType {
        if (column.startsWith('seller_id')) {
            return SellerSnapshotDatasourceFieldType.Seller;
        } else if (column.startsWith('tag_id_')) {
            return SellerSnapshotDatasourceFieldType.Tag;
        } else if (column.startsWith('qty_')) {
            return SellerSnapshotDatasourceFieldType.Qty;
        } else if (column.startsWith('date_')) {
            return SellerSnapshotDatasourceFieldType.Date;
        } else if (column.startsWith('text_') || column === 'notes') {
            return SellerSnapshotDatasourceFieldType.Text;
        } else if (column === 'customer_id') {
            return SellerSnapshotDatasourceFieldType.Customer;
        } else if (column === 'product_id') {
            return SellerSnapshotDatasourceFieldType.Product;
        } else {
            return SellerSnapshotDatasourceFieldType.Unknown;
        }
    }

    onFilterBuilderContentReady(e: any): void {
        this.helperService.onFilterBuilderContentReady(e, this.filterBuilderPlaceholderFieldCaption);
    }

    customizeComboBoxText(fieldInfo: any): string {
        const fieldProps = this.getFilterBuilderFieldProperties(fieldInfo.field.dataField);
        const item = fieldProps.lookup.dataSource.staticItems.find(x => x.id === fieldInfo.value);
        return item ? item.name : fieldInfo.value;
    }

    customColumn(e: any, condition: any){
        e.customItem = e.text;
        condition.text = e.customItem;
        condition.value = e.customItem;
        condition.setValue(e.customItem);
    }

    retainCustomValue(newValue: any, condition: any){
        if(newValue){
            condition.value = newValue;
            condition.setValue(newValue);
        }
    }

    handleEnterKey(e: any){
        e.component.blur();
    }
}
